/**
 * A rota do Negócio (3.10; modal desde a 11.4).
 *
 *     /negocios/$negocioId    o modal do Negócio sobre a tela Hoje
 *
 * **Desde a 11.4 o Negócio é modal**, aberto **sobre a tela de origem** (Funil, Hoje, …). De dentro
 * do app ele é aberto pelo parâmetro `?negocio=` da própria tela, com a barra mascarada como
 * `/negocios/<id>` (`modulos/negocios/navegacao-do-negocio.tsx`) — esta rota não entra nesse
 * caminho. Ela atende o **endereço real**: o link colado, a página recarregada, a nova aba. Sem tela
 * de origem na pilha, o fundo é **Hoje** (Design Notes da spec) e fechar leva a ela.
 *
 * **`CAMINHOS` não muda**: o Negócio não é item de navegação. O `key={negocioId}` remonta o modal
 * ao trocar de `id` — o rascunho dos campos e do compositor não atravessa de um Negócio a outro.
 */

import { createRoute, useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'

import { ModalDoNegocio } from '@/modulos/negocios/ModalDoNegocio'
import { useDefinirTitulo } from '@/shell/Shell'
import { CAMINHOS } from '@/shell/navegacao'
import { rotaDaAplicacao } from './aplicacao'
import { rotaDeHoje } from './hoje'

/** O caminho real do Negócio. Escrito uma vez; quem precisa dele o importa. */
export const CAMINHO_DO_NEGOCIO = '/negocios/$negocioId'

/** O título do shell sob o modal: o da tela de fundo, Hoje. */
const TITULO_DO_FUNDO = 'Hoje'

function TelaDoNegocio() {
  const { negocioId } = rotaDoNegocio.useParams()
  const navegar = useNavigate()
  /* A tela de fundo é a de Hoje — o mesmo componente da rota `/hoje`, e não uma cópia. */
  const Fundo = rotaDeHoje.options.component
  /* O cabeçalho do shell é o da tela de fundo. */
  useDefinirTitulo(TITULO_DO_FUNDO)

  /* Estáveis: o modal os usa em efeito (Esc), e uma identidade nova a cada render refaria o efeito
     e faria o foco pular. */
  const fechar = useCallback(() => {
    void navegar({ to: CAMINHOS.hoje, replace: true })
  }, [navegar])
  /* "Ver no funil": o quadro do Funil deste Negócio, com o modal por cima (o mesmo caminho de dentro
     do app — `?negocio=` e a barra mascarada). */
  const verNoFunil = useCallback(
    (funilId: string) => {
      void navegar({
        to: CAMINHOS.funil,
        search: { funil: funilId, negocio: negocioId },
        mask: { to: '/negocios/$negocioId', params: { negocioId } },
      })
    },
    [navegar, negocioId],
  )

  return (
    <>
      {Fundo === undefined ? null : <Fundo />}
      <ModalDoNegocio
        key={negocioId}
        negocioId={negocioId}
        aoFechar={fechar}
        aoVerNoFunil={verNoFunil}
      />
    </>
  )
}

export const rotaDoNegocio = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHO_DO_NEGOCIO,
  component: TelaDoNegocio,
})
